import { useState } from "react"
import { cn } from "@/lib/utils"
import type { OnboardingGoalId } from "@/lib/types/onboarding-goal"
import { examplesForGoal, type OnboardingProofExample } from "@/lib/config/onboardingProofExamples"

/**
 * "What others tested": four published A/B tests, picked for the goal, each
 * with an A/B composite, the change in one line, and the result.
 *
 * Deliberately a different shape from the user's draft cards above it (one
 * image, not a pair of frames; company name first; teal result) so a public
 * result never reads as the user's own. The sources are named once, in the
 * note under the grid, with the line that these are other companies' results
 * (founder, 2026-09-10): no per-card "via" label.
 */
type OnboardingExampleTestsProps = {
  goal: OnboardingGoalId | null
  /** True while the crawl is still running: the heading says so. */
  pending?: boolean
  /** Unique per instance: the plan renders one inline (< lg) and one in its own column (lg+). */
  headingId?: string
  className?: string
}

export function OnboardingExampleTests({ goal, pending = false, headingId = "onboarding-examples-heading", className }: OnboardingExampleTestsProps) {
  const examples = examplesForGoal(goal)

  return (
    <section aria-labelledby={headingId} className={cn("space-y-2.5", className)}>
      <h2 id={headingId} className="text-xs font-medium text-foreground">
        {pending ? "What others tested, while we read your page" : "What others tested"}
      </h2>
      <ul className="m-0 grid list-none grid-cols-1 gap-2.5 p-0 sm:grid-cols-2">
        {examples.map((ex) => (
          <li key={ex.id}>
            <ExampleCard example={ex} />
          </li>
        ))}
      </ul>
      <p className="text-[11px] leading-snug text-muted-foreground">
        Published tests by other companies, collected from {sourceList(examples)}. Their results, not
        Humblytics customers. Shown so you can see the size of change a first test usually is.
      </p>
    </section>
  )
}

const SOURCE_LABEL: Record<OnboardingProofExample["source"], string> = {
  "abtest.design": "abtest.design",
  hubspot: "HubSpot's blog",
  abtestcases: "AB Test Cases",
}

/** "abtest.design and HubSpot's blog", in first-seen order. */
export function sourceList(examples: OnboardingProofExample[]): string {
  const names = [...new Set(examples.map((e) => SOURCE_LABEL[e.source]))]
  if (names.length <= 1) return names[0] ?? "published research"
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
}

function ExampleCard({ example }: { example: OnboardingProofExample }) {
  const [loaded, setLoaded] = useState(false)
  return (
    <div className="rounded-lg border border-border/50 bg-card p-2">
      <div className="relative aspect-[16/9] overflow-hidden rounded-md bg-muted/40">
        <img
          src={example.image}
          alt={`${example.company} A/B test, before and after`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          className={cn(
            "h-full w-full object-cover object-center transition-opacity duration-300 motion-reduce:transition-none",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      </div>
      <div className="mt-2 space-y-0.5 px-0.5">
        <p className="text-xs font-medium leading-tight text-foreground">{example.company}</p>
        <p className="text-[11px] leading-snug text-muted-foreground">{example.change}</p>
        <p className="text-[11px] font-medium tabular-nums text-foreground">{example.result}</p>
      </div>
    </div>
  )
}
